import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import {
  ICoreDataEntity,
  ObjectKey,
  Query,
  QueryColumnSortOrder,
  QueryObjectsModel,
  QueryOutputMode,
  ReadListDataSourceModel,
} from '@barthauer/abc/core/data';
import { AbcExpressionsEditorService } from '@barthauer/abc/queries/opath/expressions-editor';
import { GridAdapter, PrepareDataSourceObjectArgs, _AbcGridComponent } from '@barthauer/abc/ui-components';
import { DataSource } from 'devextreme/common/data';
import { ParameterService } from 'libs/abc/queries/parameters/values-editor/src/lib/services/parameter.service';
import { BehaviorSubject, merge, Observable, of, Subject } from 'rxjs';
import { catchError, map, mapTo, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { GroupSelectionService } from '../group-selection.service';
import { ObjectSelectionGroup, ReadListDataSourceInfo } from '../group-selection/group-selection.model';
import { SimpleObjectQueryInfo } from './../group-selection/group-selection.model';
import { ObjectSelectionGroupData } from './object-selection.model';

@Component({
  selector: 'abc-object-selection',
  templateUrl: './object-selection.component.html',
  styleUrls: ['./object-selection.component.scss'],
  standalone: false,
})
export class ObjectSelectionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public group?: ObjectSelectionGroup;
  @Output() newItemEvent = new EventEmitter<number>();
  @Output() selectedItemsCount = new EventEmitter<number>();
  @Output() selectRowsByKeys: (rows: {
    idExpression: string;
    objectTypeExpression: string;
    entities: ICoreDataEntity[];
  }) => void;

  public selectionChanged$ = new Subject<{
    selected: any[];
  }>();

  @ViewChild('objectSelectionGrid', { read: _AbcGridComponent, static: false })
  objectSelectionGrid!: _AbcGridComponent;

  groupData$!: Observable<ObjectSelectionGroupData>;
  private readonly refreshGroupDataToken$ = new BehaviorSubject(undefined);
  isLoadingGroupData$!: Observable<boolean>;
  public keyExpr: string = 'Id';
  private gridAdapter: GridAdapter;
  private suppressSelectionEvent: boolean = false;

  constructor(
    private abcCore: AbcCore,
    private groupSelectionService: GroupSelectionService,
    private opathExpressionEditorService: AbcExpressionsEditorService,
    private parameterService: ParameterService,
  ) {}

  ngOnInit(): void {
    this.groupData$ = this.refreshGroupDataToken$.pipe(
      switchMap(() =>
        this.queryGroupsData().pipe(
          catchError((err) => {
            this.abcCore.error.publish(err);
            return of(null as unknown as ObjectSelectionGroupData);
          }),
        ),
      ),
    );

    this.isLoadingGroupData$ = merge(
      this.refreshGroupDataToken$.pipe(mapTo(true)),
      this.groupData$.pipe(
        // Even errors indicate we're not still loading.
        catchError(() => of(undefined)),
        mapTo(false),
      ),
    ).pipe(
      // tap((b) => {
      //   console.log(`IsLoading Subscription: ${b}`);
      // }),
      publishReplay(1),
      refCount(),
    );

    this.selectRowsByKeys = (obj) => {
      const idExpression = this.groupSelectionService.getIdOrOtExprName(obj.idExpression);
      const objectTypeExpression = this.groupSelectionService.getIdOrOtExprName(obj.objectTypeExpression);

      if (idExpression && objectTypeExpression) {
        this.filterEntities(obj.entities, idExpression, objectTypeExpression, false);
      }
    };
  }

  private filterEntities(
    entities: ICoreDataEntity[],
    idExpression: string,
    objectTypeExpression: string,
    fallbackToDefaultExpression: boolean,
  ) {
    const dataRows = this.objectSelectionGrid.instance.getDataSource().items();
    const filteredData = [];
    for (let index = 0; index < entities.length; index++) {
      const row = entities[index];
      const destRow = dataRows
        .filter((dr) => dr[idExpression] === row.Id)
        ?.filter((dr) => dr[objectTypeExpression] === row.ObjectType)
        .map((dr) => dr.Id);
      if (destRow.length > 0) {
        filteredData.push(destRow[0]);
      }
    }

    if (filteredData.length > 0) {
      this.objectSelectionGrid.setSelectedRowKeys(filteredData);
    } else if (!fallbackToDefaultExpression) {
      this.filterEntities(entities, 'Id', 'ObjectType', true);
    }
  }

  ngOnDestroy(): void {
    this.refreshGroupDataToken$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      const change = changes[propName];

      switch (propName) {
        case 'group': {
          const oldGroup = change.previousValue as ObjectSelectionGroup;
          const newGroup = change.currentValue as ObjectSelectionGroup;

          if (newGroup !== oldGroup) {
            if (this.groupSelectionService) {
              if (oldGroup && this.groupSelectionService.objectSelectionComponents.has(oldGroup.name)) {
                this.groupSelectionService.objectSelectionComponents.delete(oldGroup.name);
              }

              if (newGroup) {
                this.groupSelectionService.objectSelectionComponents.set(newGroup.name, this);
              }
            }

            this.refreshGroupData();
          }

          break;
        }
        default:
          break;
      }
    }
  }

  public applyExternalSelection(keys: any[]): void {
    const current = this.getSelectedKeys();
    const merged = [...new Set([...current, ...keys])];
    this.selectRowsSilently(merged);
  }

  private selectRowsSilently(keys: any[]): void {
    this.suppressSelectionEvent = true;
    this.objectSelectionGrid.setSelectedRowKeys(keys);
    this.suppressSelectionEvent = false;
  }

  onContentReady(evenArgs: any): void {
    evenArgs.component.option('loadPanel.enabled', false);
  }

  onCellPrepared(evenArgs: any): void {
    if (
      evenArgs.rowType === 'data' &&
      evenArgs.column.command === 'select' &&
      this.group?.filterForDisabledRows &&
      this.group.filterForDisabledRows(evenArgs)
    ) {
      evenArgs.cellElement.find('.dx-select-checkbox').dxCheckBox('instance').option('disabled', true);
      evenArgs.cellElement.off();
    }
  }

  onSelectionChanged(event: any): void {
    if (!this.suppressSelectionEvent) {
      this.selectionChanged$.next({
        selected: event.currentSelectedRowKeys ?? [],
      });
    }
    this.newItemEvent.emit(event.selectedRowKeys.length);
    this.selectedItemsCount.emit(this.getSelectedKeys().length);
  }

  onPrepareDataSourceObject(args: PrepareDataSourceObjectArgs): void {
    args.grid.storageSettings.includeFiltering = false;

    this.gridAdapter = new GridAdapter();
    args.getDataSource = async () => {
      await this.gridAdapter.bindGridToQuery(
        args.grid,
        this.groupSelectionService.query,
        {
          getColumns: async () => args.dataSourceObject.gridColumns,
          storage: args.dataSourceObject?.storage,
          storageKey: args.dataSourceObject?.storageKey,
          getSelectedRowKeys: (dataRows) => this.getPreselectedRowKeys(dataRows),
          initialDataSourceIsAlreadySet: true,
        },
        this.opathExpressionEditorService,
        this.parameterService.loadParameterDefinitions,
        this.parameterService.saveParameterDefinitions,
      );
      const dataRows: any[] = ConvertHelper.arrayBufferToObjectArray(
        await this.abcCore.data.executeReadObjectsQuery(this.groupSelectionService.query).toPromise(),
      );
      return dataRows;
    };
  }

  private refreshGroupData(): void {
    this.refreshGroupDataToken$.next(undefined);
  }

  private queryGroupsData(): Observable<ObjectSelectionGroupData> {
    const groupData: ObjectSelectionGroupData = new ObjectSelectionGroupData();
    groupData.storage = Promise.resolve(this.group?.layoutStorage);
    groupData.storageKey = this.group?.layoutStorageKey;

    groupData.gridColumns.push({ dataField: 'Id', visible: false, allowFiltering: false, showInColumnChooser: false });

    if (this.group?.objectTypeNameOrQueryInfo) {
      if (SimpleObjectQueryInfo.isSimpleObjectQueryInfo(this.group.objectTypeNameOrQueryInfo)) {
        groupData.gridColumns.push({
          dataField: 'ObjectType',
          visible: false,
          allowFiltering: false,
          showInColumnChooser: false,
        });
        return this.execSimpleQuery(groupData, this.group.objectTypeNameOrQueryInfo);
      } else {
        // Do not hide ObjectType columns as it display Ot display text
        return this.execListDataSourceQuery(groupData, this.group.objectTypeNameOrQueryInfo);
      }
    } else {
      return of(groupData);
    }
  }

  private execSimpleQuery(
    groupData: ObjectSelectionGroupData,
    simpleQueryInfo: SimpleObjectQueryInfo,
  ): Observable<ObjectSelectionGroupData> {
    if (simpleQueryInfo.columnHeaders) {
      for (const colHeader of simpleQueryInfo.columnHeaders) {
        let dxCol = groupData.gridColumns.find((c) => c.dataField === colHeader.dataField);

        if (!dxCol) {
          dxCol = {};
          groupData.gridColumns.push(dxCol);
        }

        Object.assign(dxCol, colHeader);
      }
    }

    const objectQuery = new QueryObjectsModel();
    objectQuery.ObjectSpaceName = simpleQueryInfo.objectSpace;
    objectQuery.OutputMode = QueryOutputMode.Json;
    objectQuery.Parameters = simpleQueryInfo.parameters ?? [];

    const query = new Query();
    objectQuery.ObjectQueries = [query];

    query.ObjectType = simpleQueryInfo.objectType;
    query.OPath = simpleQueryInfo.oPathFilter ?? 'true';
    query.DefaultIfEmpty = simpleQueryInfo.defaultIfEmpty;
    query.IsDistinct = simpleQueryInfo.isDistinct ?? false;
    query.LimitCount = simpleQueryInfo.limitCount ?? -1;
    query.LimitOffset = simpleQueryInfo.limitOffset ?? -1;

    query.Columns = [];

    if (!simpleQueryInfo.columns?.find((c) => c.Name === 'Id')) {
      query.Columns.push({ Name: 'Id', OPath: 'Id' });
    }

    if (!simpleQueryInfo.columns?.find((c) => c.Name === 'ObjectType')) {
      query.Columns.push({ Name: 'ObjectType', OPath: 'ObjectType' });
    }

    query.Columns.push(...(simpleQueryInfo.columns ?? []));

    let currentSortIndex = -1;

    for (const queryCol of query.Columns.filter((c) => c.SortOrder)) {
      const dxCol = groupData.gridColumns.find((c) => c.dataField === queryCol.Name);

      if (dxCol) {
        dxCol.sortIndex = ++currentSortIndex;
        dxCol.sortOrder = queryCol.SortOrder === QueryColumnSortOrder.Descending ? 'desc' : 'asc';
      }
    }

    this.groupSelectionService.query = objectQuery;
    groupData.dataRows = [];
    return of(groupData);
  }

  private execListDataSourceQuery(
    groupData: ObjectSelectionGroupData,
    objectTypeNameOrReadListDataSourceInfo: string | ReadListDataSourceInfo,
  ): Observable<ObjectSelectionGroupData> {
    const readListDataSourceModel = new ReadListDataSourceModel();
    readListDataSourceModel.OutputMode = QueryOutputMode.Json;

    if (ReadListDataSourceInfo.isReadListDataSourceInfo(objectTypeNameOrReadListDataSourceInfo)) {
      readListDataSourceModel.ObjectKey = objectTypeNameOrReadListDataSourceInfo.objectKey;
      readListDataSourceModel.ColumnName = objectTypeNameOrReadListDataSourceInfo.columnName;
      readListDataSourceModel.Parameters = objectTypeNameOrReadListDataSourceInfo.parameters ?? [];
    } else {
      const objectKey = new ObjectKey();
      objectKey.ObjectType = objectTypeNameOrReadListDataSourceInfo;
      readListDataSourceModel.ObjectKey = objectKey;
      readListDataSourceModel.ColumnName = 'Id';
    }

    return this.abcCore.data.executeReadListDataSource(readListDataSourceModel).pipe(
      map((data) => {
        if (data) {
          if (Array.isArray(data.ColumnInfos) && data.ColumnInfos.length) {
            for (const ci of data.ColumnInfos) {
              let dxCol = groupData.gridColumns.find((c) => c.dataField === ci.Name);

              if (!dxCol) {
                dxCol = { dataField: ci.Name };
                groupData.gridColumns.push(dxCol);
              }

              dxCol.caption = ci.Caption ?? ci.Name;
              dxCol.visible = !(ci.hidden ?? false);
            }
          }

          if (Array.isArray(data.Data) && data.Data.length) {
            groupData.dataRows = data.Data;
            groupData.selectedRowKeys = this.getPreselectedRowKeys(groupData.dataRows);
          }
        }

        return groupData;
      }),
    );
  }

  /**
   * Gets all selected rows with all fields, including calculated unbound columns.
   * @returns An array of JSON objects with dataField names as keys.
   */
  public getSelectedGridRows(onlyDataFields?: string[]): any[] {
    const dataFields = this.objectSelectionGrid.columns
      .map((col) => (typeof col === 'string' ? col : (col.dataField as string)))
      .filter((df) => (onlyDataFields ? onlyDataFields.indexOf(df) > -1 : true));

    const dataRows = [];
    for (const rowKey of this.getSelectedKeys()) {
      const rowIndex = this.objectSelectionGrid.instance.getRowIndexByKey(rowKey);
      const dataRow: any = {};

      for (const dataField of dataFields) {
        dataRow[dataField] = this.objectSelectionGrid.instance.cellValue(rowIndex, dataField);
      }
      dataRows.push(dataRow);
    }
    return dataRows;
  }

  /**
   * Gets all selected rows with all data bound fields, excluding calculated unbound columns.
   * @returns An array of JSON objects with dataField names as keys.
   */
  public getSelectedDataRows(): any[] {
    return this.objectSelectionGrid.instance.getSelectedRowsData() as any[]; // selection is not deferred
  }

  /**
   * Gets all key values (e.g. ids) of all selected rows, excluding calculated unbound columns.
   * @returns An array of row key values.
   */
  public getSelectedKeys(): string[] {
    return this.objectSelectionGrid.instance.getSelectedRowKeys() as string[]; // selection is not deferred
  }

  public getPreselectedRowKeysFunc: (dataRows: any[] | DataSource<any, any>) => any[] = (
    dataRows: any[] | DataSource<any, any>,
  ) => {
    return this.getPreselectedRowKeys(dataRows);
  };

  private getPreselectedRowKeys(dataRows: any[] | DataSource<any, any>): any[] {
    const result: any[] = [];
    if (this.group?.preselectedKeys?.length && Array.isArray(dataRows) && dataRows?.length) {
      for (const row of dataRows) {
        const id = row[this.keyExpr];
        let key = id;
        if (this.group?.preselectionKeyExpr && this.group?.preselectionKeyExpr !== this.keyExpr) {
          key = row[this.group?.preselectionKeyExpr];
        }
        if (key && this.group?.preselectedKeys.includes(key)) {
          result.push(id);
        }
      }
    }
    return result;
  }
}
